import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import '../styles/JoinModal.scss';

// Use the existing reCAPTCHA site key directly
const RECAPTCHA_SITE_KEY = '6LdleMEqAAAAACjtGkwMXYO2fQXJoc-VpRQXSjpu';

const JoinModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    teamName: '',
    teamType: '',
    isOnFirstWhistle: '',
    managerName: '',
    managerMobile: '',
    ageGroup: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const modalRef = useRef(null);
  
  // Setup reCAPTCHA on component mount
  useEffect(() => {
    // Check if reCAPTCHA script is already loaded
    if (!window.grecaptcha) {
      // Load the script if not already loaded
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api/js?render=${RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      
      // Add load event listeners
      script.onload = () => {
        console.log('reCAPTCHA script loaded successfully');
      };
      
      script.onerror = (error) => {
        console.error('Error loading reCAPTCHA script:', error);
      };
      
      document.head.appendChild(script);

      return () => {
        try {
          document.head.removeChild(script);
        } catch (e) {
          console.warn('Error removing reCAPTCHA script:', e);
        }
      };
    }
  }, []);

  // Handle ResizeObserver error
  useEffect(() => {
    // This prevents the ResizeObserver loop limit exceeded error
    const errorHandler = (event) => {
      if (event.message && (
        event.message.includes('ResizeObserver loop') || 
        event.message.includes('ResizeObserver Loop')
      )) {
        event.stopImmediatePropagation();
        event.preventDefault();
        console.debug('ResizeObserver error suppressed');
      }
    };

    window.addEventListener('error', errorHandler, true);
    window.addEventListener('unhandledrejection', errorHandler, true);
    
    return () => {
      window.removeEventListener('error', errorHandler, true);
      window.removeEventListener('unhandledrejection', errorHandler, true);
    };
  }, []);

  // Execute reCAPTCHA and get token
  const executeRecaptcha = async () => {
    try {
      // Wait for grecaptcha to be available if not loaded yet
      let attempts = 0;
      while (!window.grecaptcha && attempts < 5) {
        await new Promise(resolve => setTimeout(resolve, 500));
        attempts++;
      }
      
      if (window.grecaptcha) {
        return await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'join_form_submit' });
      }
      
      console.log('reCAPTCHA not loaded after waiting, using fallback token');
      return 'direct-signup-page'; // Fallback token
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      return 'direct-signup-page'; // Fallback token on error
    }
  };

  useEffect(() => {
    // Prevent scrolling on body when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close modal when clicking escape key
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) onClose();
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  // Use this for smoother modal rendering
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Allow browser to position and render elements before animations
      requestAnimationFrame(() => {
        if (modalRef.current) {
          modalRef.current.style.opacity = '0';
          requestAnimationFrame(() => {
            if (modalRef.current) {
              modalRef.current.style.opacity = '1';
            }
          });
        }
      });
    }
  }, [isOpen]);

  // Add smooth transition for form field changes
  useEffect(() => {
    // Delay showing additional fields to avoid rendering issues
    if (formData.isOnFirstWhistle) {
      // Give the DOM time to settle before showing additional fields
      requestAnimationFrame(() => {
        // Force a repaint to avoid layout issues
        document.body.style.opacity = '0.99999';
        requestAnimationFrame(() => {
          document.body.style.opacity = '';
        });
      });
    }
  }, [formData.isOnFirstWhistle]);

  // Use layout effect to stabilize DOM before ResizeObserver works
  useLayoutEffect(() => {
    if (isOpen && modalRef.current) {
      // Force a layout pass before animations start
      const height = modalRef.current.offsetHeight;
      modalRef.current.style.minHeight = `${height}px`;
      
      return () => {
        if (modalRef.current) {
          modalRef.current.style.minHeight = '';
        }
      };
    }
  }, [isOpen, formData.isOnFirstWhistle, formData.teamType]);

  // Add a specific fix for ResizeObserver issues
  useEffect(() => {
    // Detect ResizeObserver loops and force a delay
    let timeoutId;
    let errorCount = 0;
    
    const errorHandler = (event) => {
      // Catch ResizeObserver loop errors
      if (event.message && event.message.includes('ResizeObserver')) {
        event.stopImmediatePropagation();
        event.preventDefault();
        
        // Count errors and force a layout stabilization if too many
        errorCount++;
        if (errorCount > 2) {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            // Force a browser reflow to stabilize layout
            if (modalRef.current) {
              modalRef.current.style.display = 'none';
              void modalRef.current.offsetHeight; // trigger reflow
              modalRef.current.style.display = '';
            }
            errorCount = 0;
          }, 100);
        }
        
        return true;
      }
    };
    
    window.addEventListener('error', errorHandler, true);
    
    return () => {
      window.removeEventListener('error', errorHandler, true);
      clearTimeout(timeoutId);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for isOnFirstWhistle to reduce layout shifts
    if (name === 'isOnFirstWhistle') {
      // For the not-sure option, need special handling
      if (value === 'not-sure') {
        // Force a small delay before updating state
        requestAnimationFrame(() => {
          setFormData(prevData => ({
            ...prevData,
            [name]: value
          }));
        });
      } else {
        // Set a small timeout to let the UI render smoothly
        setTimeout(() => {
          setFormData(prevData => ({
            ...prevData,
            [name]: value,
            // Reset manager fields if changing from "No"/"Not sure" to "Yes"
            ...(value === 'true' && (prevData.isOnFirstWhistle === 'false' || prevData.isOnFirstWhistle === 'not-sure') 
                ? { managerName: '', managerMobile: '' } 
                : {})
          }));
        }, 10);
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Clear the error for this field when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
    
    // Reset submit status when form is changed
    if (submitStatus) {
      setSubmitStatus(null);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.teamType) {
      errors.teamType = 'Please select an option';
      setFormErrors(errors);
      return false;
    }
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!formData.mobile.trim()) {
      errors.mobile = 'Mobile number is required';
    } else if (!/^[0-9\s+()-]{10,15}$/.test(formData.mobile.trim())) {
      errors.mobile = 'Please enter a valid mobile number';
    }
    
    if (!formData.teamName.trim()) {
      errors.teamName = 'Team name is required';
    }
    
    // Show age group field for "Coach (Academy)" user type
    if (formData.teamType === 'Coach (Academy)' && !formData.ageGroup.trim()) {
      errors.ageGroup = 'Age group is required';
    }
    
    // Show the isOnFirstWhistle field for "Player" or "Parent" user types
    if ((formData.teamType === 'Player' || formData.teamType === 'Parent') && !formData.isOnFirstWhistle) {
      errors.isOnFirstWhistle = 'Please select an option';
    }
    
    // Show manager fields only if user selected "No" or "Not sure" to their team being on FirstWhistle
    if ((formData.isOnFirstWhistle === 'false' || formData.isOnFirstWhistle === 'not-sure') && 
        (formData.teamType === 'Player' || formData.teamType === 'Parent')) {
      if (!formData.managerName.trim()) {
        errors.managerName = 'Manager name is required';
      }
      
      if (!formData.managerMobile.trim()) {
        errors.managerMobile = 'Manager mobile number is required';
      } else if (!/^[0-9\s+()-]{10,15}$/.test(formData.managerMobile.trim())) {
        errors.managerMobile = 'Please enter a valid mobile number';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Get reCAPTCHA token
      const recaptchaToken = await executeRecaptcha();
      
      // Even if token is null, continue with submission using a fallback token
      const tokenToUse = recaptchaToken || 'direct-signup-page';
      
      // Send data to the API endpoint - use relative path for local development
      const response = await fetch('/api/join-waitlist.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          captchaToken: tokenToUse
        }),
      });
      
      let data;
      try {
        const textResponse = await response.text();
        console.log('Raw response:', textResponse);
        
        try {
          data = JSON.parse(textResponse);
        } catch (parseError) {
          console.error('Error parsing response:', parseError);
          // If JSON parsing fails, create a simple object with the raw response
          data = { success: false, message: textResponse };
        }
      } catch (error) {
        console.error('Error reading response:', error);
        throw new Error('Could not read server response');
      }
      
      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }
      
      // Success handling
      setSubmitStatus('success');
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to check if we need to show the FirstWhistle check
  const shouldShowFirstWhistleCheck = () => {
    return formData.teamType === 'Parent' || formData.teamType === 'Player';
  };

  // Preload and cache field visibility function - more reliable than conditional rendering
  const getFieldVisibility = (fieldType) => {
    // If no teamType selected, hide all fields
    if (!formData.teamType) return 'hidden';
    
    // If no answer to club question yet, hide manager fields
    if (!formData.isOnFirstWhistle && ['managerName', 'managerMobile'].includes(fieldType)) return 'hidden';
    
    // Different cases based on the response to "Club is on FirstWhistle"
    switch (formData.isOnFirstWhistle) {
      case 'true':
        // Yes - hide manager fields
        return (fieldType === 'managerName' || fieldType === 'managerMobile') ? 'hidden' : 'visible';
      case 'false':
      case 'not-sure':
        // No or Not sure - show all fields
        return 'visible';
      default:
        // No answer yet - hide manager fields
        return (fieldType === 'managerName' || fieldType === 'managerMobile') ? 'hidden' : 'visible';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="join-modal" ref={modalRef} onClick={e => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        
        <div className="modal-header">
          <h2>Join FirstWhistle</h2>
          <p>Fill in your details to get early access</p>
          
          <div className="user-type-selection">
            <label htmlFor="teamType">I am a...</label>
            <select
              id="teamType"
              name="teamType"
              value={formData.teamType}
              onChange={handleChange}
              className={formErrors.teamType ? 'error' : ''}
            >
              <option value="">Select option</option>
              <option value="Player">Player</option>
              <option value="Casual (Plays weekly, non-league)">Casual (Plays weekly, non-league)</option>
              <option value="Coach (Academy)">Coach (Academy)</option>
              <option value="Manager - Sat / Sun league">Manager - Sat / Sun league</option>
              <option value="Parent">Parent</option>
            </select>
            {formErrors.teamType && <span className="error-message">{formErrors.teamType}</span>}
          </div>
        </div>
        
        {submitStatus === 'success' ? (
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h3>Thank you for joining FirstWhistle!</h3>
            {formData.teamType === 'Parent' && formData.isOnFirstWhistle === 'true' && (
              <p>Great to have you on board! We will confirm with the club and approve access - we will be in touch soon.</p>
            )}
            {formData.teamType === 'Parent' && formData.isOnFirstWhistle === 'not-sure' && (
              <p>Thanks for signing up! We'll check if your team is already on FirstWhistle and get back to you soon.</p>
            )}
            {formData.teamType === 'Parent' && formData.isOnFirstWhistle === 'false' && (
              <p>Thank you for your interest! We'll contact your team manager to get your club set up on FirstWhistle and then we'll be in touch.</p>
            )}
            {formData.teamType === 'Player' && formData.isOnFirstWhistle === 'true' && (
              <p>Great to have you on board! We will confirm with the club and approve access - we will be in touch soon.</p>
            )}
            {formData.teamType === 'Player' && formData.isOnFirstWhistle === 'not-sure' && (
              <p>Thanks for signing up! We'll check if your team is already on FirstWhistle and get back to you soon.</p>
            )}
            {formData.teamType === 'Player' && formData.isOnFirstWhistle === 'false' && (
              <p>Thank you for your interest! We'll contact your team manager to get your club set up on FirstWhistle and then we'll be in touch.</p>
            )}
            {(formData.teamType === 'Casual (Plays weekly, non-league)' || formData.teamType === 'Coach (Academy)' || formData.teamType === 'Manager - Sat / Sun league') && (
              <p>Welcome to FirstWhistle! We're excited to have you join us. We'll be in touch soon with information on how to set up your team.</p>
            )}
            <button 
              className="close-success-button" 
              onClick={() => {
                onClose();
                // Reset form after user closes modal
                setFormData({
                  firstName: '',
                  lastName: '',
                  mobile: '',
                  teamName: '',
                  teamType: '',
                  isOnFirstWhistle: '',
                  managerName: '',
                  managerMobile: '',
                  ageGroup: ''
                });
                setFormErrors({});
                setSubmitStatus(null);
              }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {formData.teamType && (
              <>
                {formData.teamType === 'Parent' ? (
                  <div className="signup-steps">
                    <div className="step">
                      <div className="step-number">1</div>
                      <div className="step-content">
                        <h3>Submit your details</h3>
                        <p>Fill in your information below</p>
                      </div>
                    </div>
                    <div className="step">
                      <div className="step-number">2</div>
                      <div className="step-content">
                        <h3>We contact your coach</h3>
                        <p>FirstWhistle team will reach out to your team's coach</p>
                      </div>
                    </div>
                    <div className="step">
                      <div className="step-number">3</div>
                      <div className="step-content">
                        <h3>Access is approved</h3>
                        <p>Once your coach confirms, you'll get access</p>
                      </div>
                    </div>
                    <div className="step-final">
                      <div className="ready-icon">🎉</div>
                      <h3>That's it!</h3>
                      <p>You'll be connected to your team in no time</p>
                    </div>
                  </div>
                ) : (
                  <div className="signup-steps">
                    <div className="step">
                      <div className="step-number">1</div>
                      <div className="step-content">
                        <h3>Register your team</h3>
                        <p>Fill in your details below to get started</p>
                      </div>
                    </div>
                    <div className="step">
                      <div className="step-number">2</div>
                      <div className="step-content">
                        <h3>We approve request</h3>
                        <p>Our team will review and approve your registration</p>
                      </div>
                    </div>
                    <div className="step">
                      <div className="step-number">3</div>
                      <div className="step-content">
                        <h3>You can then log in and invite your players</h3>
                        <p>Start managing your team on FirstWhistle</p>
                      </div>
                    </div>
                    <div className="step-final">
                      <div className="ready-icon">🎉</div>
                      <h3>That's it!</h3>
                      <p>Your team will be up and running in no time</p>
                    </div>
                  </div>
                )}

                <form className="join-form" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="Your first name"
                      className={formErrors.firstName ? 'error' : ''}
                    />
                    {formErrors.firstName && <span className="error-message">{formErrors.firstName}</span>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Your last name"
                      className={formErrors.lastName ? 'error' : ''}
                    />
                    {formErrors.lastName && <span className="error-message">{formErrors.lastName}</span>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="mobile">Mobile</label>
                    <input
                      type="tel"
                      id="mobile"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      placeholder="Your mobile number"
                      className={formErrors.mobile ? 'error' : ''}
                    />
                    {formErrors.mobile && <span className="error-message">{formErrors.mobile}</span>}
                  </div>
                  
                  {shouldShowFirstWhistleCheck() ? (
                    <>
                      <div className="form-group">
                        <label htmlFor="isOnFirstWhistle">My Club is already on FirstWhistle</label>
                        <select
                          id="isOnFirstWhistle"
                          name="isOnFirstWhistle"
                          value={formData.isOnFirstWhistle}
                          onChange={handleChange}
                          className={formErrors.isOnFirstWhistle ? 'error' : ''}
                        >
                          <option value="">Select option</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                          <option value="not-sure">Not sure</option>
                        </select>
                        {formErrors.isOnFirstWhistle && <span className="error-message">{formErrors.isOnFirstWhistle}</span>}
                      </div>
                      
                      {/* Always render these fields but control visibility with CSS */}
                      <div className="form-fields-container">
                        <div className="form-group" style={{visibility: formData.isOnFirstWhistle ? 'visible' : 'hidden', height: formData.isOnFirstWhistle ? 'auto' : '0'}}>
                          <label htmlFor="teamName">Team Name</label>
                          <input
                            type="text"
                            id="teamName"
                            name="teamName"
                            value={formData.teamName}
                            onChange={handleChange}
                            placeholder="Your team name"
                            className={formErrors.teamName ? 'error' : ''}
                          />
                          {formErrors.teamName && <span className="error-message">{formErrors.teamName}</span>}
                        </div>
                        
                        <div className={`manager-fields ${(formData.isOnFirstWhistle === 'false' || formData.isOnFirstWhistle === 'not-sure') ? 'visible' : 'hidden'}`}>
                          <div className="form-group">
                            <label htmlFor="managerName">Manager Name</label>
                            <input
                              type="text"
                              id="managerName"
                              name="managerName"
                              value={formData.managerName}
                              onChange={handleChange}
                              placeholder="Team manager's name"
                              className={formErrors.managerName ? 'error' : ''}
                            />
                            {formErrors.managerName && <span className="error-message">{formErrors.managerName}</span>}
                          </div>
                          
                          <div className="form-group">
                            <label htmlFor="managerMobile">Manager Mobile</label>
                            <input
                              type="tel"
                              id="managerMobile"
                              name="managerMobile"
                              value={formData.managerMobile}
                              onChange={handleChange}
                              placeholder="Team manager's mobile number"
                              className={formErrors.managerMobile ? 'error' : ''}
                            />
                            {formErrors.managerMobile && <span className="error-message">{formErrors.managerMobile}</span>}
                          </div>
                          
                          <div className="info-message">
                            <p>{formData.isOnFirstWhistle === 'false' 
                              ? "As your team is currently not on FirstWhistle, we need to contact your club." 
                              : "We'll check if your team is on FirstWhistle and contact your manager if needed."}</p>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="form-group">
                        <label htmlFor="teamName">Team Name</label>
                        <input
                          type="text"
                          id="teamName"
                          name="teamName"
                          value={formData.teamName}
                          onChange={handleChange}
                          placeholder="Your team name"
                          className={formErrors.teamName ? 'error' : ''}
                        />
                        {formErrors.teamName && <span className="error-message">{formErrors.teamName}</span>}
                      </div>
                      
                      {formData.teamType === 'Coach (Academy)' && (
                        <div className="form-group">
                          <label htmlFor="ageGroup">Age Group</label>
                          <select
                            id="ageGroup"
                            name="ageGroup"
                            value={formData.ageGroup}
                            onChange={handleChange}
                            className={formErrors.ageGroup ? 'error' : ''}
                          >
                            <option value="">Select age group</option>
                            <option value="U6">U6</option>
                            <option value="U7">U7</option>
                            <option value="U8">U8</option>
                            <option value="U9">U9</option>
                            <option value="U10">U10</option>
                            <option value="U11">U11</option>
                            <option value="U12">U12</option>
                            <option value="U13">U13</option>
                            <option value="U14">U14</option>
                            <option value="U15">U15</option>
                            <option value="U16">U16</option>
                            <option value="U17">U17</option>
                            <option value="U18">U18</option>
                            <option value="U19+">U19+</option>
                          </select>
                          {formErrors.ageGroup && <span className="error-message">{formErrors.ageGroup}</span>}
                        </div>
                      )}
                    </>
                  )}
                  
                  <div className="recaptcha-info">
                    <p>This site is protected by reCAPTCHA v3.</p>
                  </div>
                  
                  <button 
                    type="submit" 
                    className="submit-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Join Now'}
                  </button>
                  
                  {submitStatus === 'error' && (
                    <div className="error-message submit-error">
                      There was an error submitting your details. Please try again.
                    </div>
                  )}
                </form>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default JoinModal; 